// La rémunération d'une offre, sources à l'appui (issue #208, lot 3 du plan,
// `docs/analyse-package-entretien.md`). Radar seulement.
//
// La pastille salaire de l'en-tête porte le geste (« Comparer », ou « Salaire
// non annoncé · Estimer ») et ouvre ce bloc, sous l'avis, avant « Vous
// postulez ? ». La recherche se lance d'ici, d'un bouton qui dit ce qu'elle
// va chercher : elle coûte, et se compte au mois.
//
// TROIS COUCHES, JAMAIS MÉLANGÉES (career-ops, Block D) : ce que l'offre dit,
// mot pour mot ; le minimum du secteur ; les repères du marché. Chaque fait
// porte sa source et sa date ; « Sources » replié dit ce qui a été écarté et
// pourquoi. Une fourchette ne s'affiche que si le serveur l'a calculée de
// deux repères relus qui s'accordent. Pas de net, aucun graphique ni jauge.

import { useEffect } from 'react'
import { Coins } from 'lucide-react'
import type { SalaireVerifie, SourceRelue } from '@ai-job-fit/engine/rechercheSourcee'
import type { Periode } from '@ai-job-fit/engine/remuneration'
import { convertir, quatreCinquiemes } from '@ai-job-fit/engine/remuneration'
import { get, post } from '../lib/api'
import { useT } from '../lib/i18n'
import { useLang } from '../lib/lang'
import { LOCALE_UI } from '@ai-job-fit/engine/langues'
import { parseDate } from '../lib/format'
import { useData, ActionButton, useToast } from './common'

export interface EtatSalaire {
  etat: 'pret' | 'en_cours' | 'erreur'
  erreur: string | null
  salaire: (SalaireVerifie & { creeLe: string }) | null
  permis: { limite: number | null; utilisees: number; restantes: number | null; permis: boolean } | null
  partiel: boolean
}

/** Rafraîchissement pendant une recherche : elle dure 30 à 110 s. */
const ATTENTE_MS = 5000

/** L'état de la rémunération d'une offre pour un profil, relu pendant qu'une
 *  recherche tourne. `profilId` nul : rien n'est demandé. */
export function useSalaire(offreId: number, profilId: number | null) {
  const d = useData(() => {
    if (profilId === null) return Promise.resolve(null)
    return get<EtatSalaire>(`/api/offers/${offreId}/salaire?${new URLSearchParams({ consultantId: String(profilId) })}`)
  }, [offreId, profilId])
  const enCours = d.data?.etat === 'en_cours'
  useEffect(() => {
    if (!enCours) return
    const i = window.setInterval(() => d.reload(), ATTENTE_MS)
    return () => window.clearInterval(i)
  }, [enCours])
  const lancer = async () => {
    if (profilId === null) return
    await post(`/api/offers/${offreId}/salaire`, { consultantId: profilId })
    d.reload()
  }
  return { ...d, lancer }
}

/** Le geste est offert en Radar, à toute formule : sans recherche possible,
 *  le bloc dit ce qui l'ouvre (`CompteurRecherches`). */
export function gesteSalaireOffert(e: EtatSalaire | null | undefined): boolean {
  return !!e?.permis
}

export type PermisRecherches = EtatSalaire['permis']

/** Peut-on lancer une recherche maintenant ? */
export const peutChercher = (p: PermisRecherches) => !p || (p.limite !== 0 && p.permis)

/**
 * Sous le bouton (issue #210) : ce qui reste ce mois-ci. Rien à volonté ;
 * « Encore 1 ce mois-ci. » ; au bout, « Plus d'essai ce mois-ci. À volonté
 * avec Complet. » ; sans formule qui en donne, « Avec Radar Complet. ».
 * Salaire et entretien sourcé se comptent ensemble.
 */
export function CompteurRecherches({ permis }: { permis: PermisRecherches }) {
  const t = useT()
  if (!permis) return null
  if (permis.limite === 0) {
    return <p className="field-help"><a href="#/abonnement">{t('remu.avecComplet')}</a></p>
  }
  if (permis.limite === null) return permis.permis ? null : <p className="field-help">{t('remu.plusDEssai')}</p>
  if (permis.restantes) return <p className="field-help">{t('remu.encore', { n: permis.restantes })}</p>
  return (
    <p className="field-help">
      {t('remu.plusDEssai')} <a href="#/abonnement">{t('remu.aVolonte')}</a>
    </p>
  )
}

/** Les montants, tous en BRUT ANNUEL dans la devise du poste (retour de
 *  Nicolas du 2026-10-16 : « je postule, j'ai autant de brut annuel »). Les
 *  conversions suivent les conventions du pays (`conventionsDe`). */
function useMontant(pays: string, devise: string) {
  const t = useT()
  const lang = useLang()
  const format = new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : LOCALE_UI[lang], { style: 'currency', currency: devise, maximumFractionDigits: 0 })
  const nombre = (n: number) => format.format(Math.round(n / 100) * 100).replace(/[\s\u202f]/g, '\u00a0')
  // En français, la devise ne se dit qu'une fois : « 79 600 à 121 900 CHF ».
  const sansDevise = (n: number) =>
    lang === 'fr' ? (Math.round(n / 100) * 100).toLocaleString('fr-FR').replace(/[\s\u202f]/g, '\u00a0') : nombre(n)
  const an = (n: number, p: Periode) => convertir(n, p, 'an', pays)
  const parAn = t('remu.parAn')
  return {
    /** « 31 200 € brut par an ». */
    un: (n: number, p: Periode) => `${nombre(an(n, p))} ${parAn}`,
    /** « 62 600 à 88 400 CHF brut par an », « Environ 64 400 € brut par an ». */
    fourchette: (m: { min: number; max: number; periode: Periode }) => {
      const a = an(m.min, m.periode)
      const b = an(m.max, m.periode)
      return Math.abs(a - b) < 100
        ? `${t('remu.environ', { montant: nombre(a) })} ${parAn}`
        : `${t('remu.entre', { a: sansDevise(a), b: nombre(b) })} ${parAn}`
    },
  }
}

export const domaine = (url: string | null) => {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : ''
  } catch {
    return ''
  }
}

export function LigneSource({ s }: { s: SourceRelue }) {
  const t = useT()
  const lang = useLang()
  const qui = s.origine === 'web' ? (
    <a href={s.url ?? undefined} target="_blank" rel="noopener noreferrer" title={s.extrait}>{domaine(s.url)}</a>
  ) : (
    t('remu.sourceOffre')
  )
  const date = s.date ? new Date(`${s.date}T12:00:00Z`).toLocaleDateString(LOCALE_UI[lang], { day: 'numeric', month: 'long', year: 'numeric' }) : null
  // Le lien est un élément : la phrase se compose autour, sans le traduire.
  const [avant, apres] = (date ? t('remu.sourceDatee', { qui: '\u0000', date }) : t('remu.source', { qui: '\u0000' })).split('\u0000')
  return <div className="remu-src field-help">{avant}{qui}{apres}</div>
}

export function BlocRemuneration({ etat, onLancer }: { etat: EtatSalaire | null; onLancer: () => Promise<void> }) {
  const t = useT()
  const lang = useLang()
  const toast = useToast()
  const s = etat?.salaire ?? null
  // Une estimation d'avant le pays : la Belgique et l'euro.
  const pays = s?.pays ?? 'BE'
  const montant = useMontant(pays, s?.devise ?? 'EUR')
  const nomPays = (() => {
    try {
      return new Intl.DisplayNames([lang], { type: 'region' }).of(pays) ?? pays
    } catch {
      return pays
    }
  })()
  const lancer = async () => {
    try {
      await onLancer()
    } catch (e: any) {
      toast(e.message, 'err')
    }
  }

  let corps
  if (etat?.etat === 'en_cours') {
    corps = (
      <p className="remu-attente" role="status">
        <span className="spinner" />{t('remu.enCours')}
      </p>
    )
  } else if (!s) {
    corps = (
      <>
        {etat?.etat === 'erreur' && <p className="remu-alerte">{t('remu.erreur')}</p>}
        <p className="field-help">{t('remu.aide')}</p>
        {peutChercher(etat?.permis ?? null) && (
          <div className="relance-act">
            <ActionButton variant="primary" onClick={lancer}>
              {etat?.etat === 'erreur' ? t('remu.reessayer') : t('remu.lancer')}
            </ActionButton>
          </div>
        )}
        <CompteurRecherches permis={etat?.permis ?? null} />
      </>
    )
  } else {
    const composantes = s.composantes ?? s.avantages.map((a) => ({ ...a, type: 'avantage' as const }))
    const retenues = [s.annonce?.source, s.minimum?.source, ...s.reperes.map((r) => r.source), ...composantes.map((a) => a.source)].filter(
      (x): x is SourceRelue => !!x && x.origine === 'web'
    )
    const cree = parseDate(s.creeLe)
    corps = (
      <>
        <p className="field-help">{t('remu.cadre', { pays: nomPays })}</p>
        {!s.employeurReel && <p className="field-help">{t('remu.agence')}</p>}
        {s.fiabilite && (
          <div className="remu-couche">
            <b>{t('remu.fiabiliteTitre')}</b>
            <p>
              {t('remu.fiabilite', { type: t(`remu.type.${s.typeEmployeur ?? 'inconnu'}` as const), fiab: t(`remu.fiab.${s.fiabilite}` as const) })}
              {s.fiabilitePourquoi ? ` ${s.fiabilitePourquoi}` : ''}
            </p>
          </div>
        )}
        <div className="remu-couche">
          <b>{t('remu.offreDit')}</b>
          {s.annonce ? (
            <>
              <p className="remu-cite">
                <q>{s.annonce.texte}</q>
                {s.annonce.min && s.annonce.periode && s.annonce.periode !== 'an'
                  ? ` ${t('remu.soit', { montant: montant.fourchette({ min: s.annonce.min, max: s.annonce.max ?? s.annonce.min, periode: s.annonce.periode }) })}`
                  : ''}
              </p>
              <LigneSource s={s.annonce.source} />
            </>
          ) : (
            <p>{t('remu.offreMuette')}</p>
          )}
          {composantes.length > 0 && (
            <ul className="remu-liste">
              {composantes.map((c, i) => (
                <li key={i}>
                  <b>{t(`remu.comp.${c.type}` as const)}</b> : {c.texte}
                  <LigneSource s={c.source} />
                </li>
              ))}
            </ul>
          )}
        </div>
        {s.minimum && (
          <div className="remu-couche">
            <b>{t('remu.minimum')}</b>
            <p>
              {t('remu.auMoins', {
                qui: [s.minimum.commission, s.minimum.categorie].filter(Boolean).join(', '),
                montant: montant.un(s.minimum.montant, s.minimum.periode),
              })}
            </p>
            {s.sousLeMinimum && (
              <p className="remu-alerte">{t(s.sousLeMinimum.sous === 'entierement' ? 'remu.sousEntierement' : 'remu.sousEnPartie')}</p>
            )}
            <LigneSource s={s.minimum.source} />
          </div>
        )}
        <div className="remu-couche">
          <b>{t('remu.marche')}</b>
          {s.fourchette ? (
            <>
              <p className="remu-fourchette">{t('remu.fourchette', { fourchette: montant.fourchette(s.fourchette), n: s.fourchette.sur })}</p>
              {etat?.partiel && <p>{t('remu.quatreCinquiemes', { fourchette: montant.fourchette(quatreCinquiemes(s.fourchette)) })}</p>}
            </>
          ) : (
            <p>{t(s.reperes.length === 1 ? 'remu.unRepere' : s.reperes.length ? 'remu.pasDeFourchette' : 'remu.aucunRepere')}</p>
          )}
          {s.reperes.length > 0 && (
            <ul className="remu-liste">
              {s.reperes.map((r, i) => (
                <li key={i}>
                  {montant.fourchette(r)}{[r.poste, r.ceQueCEst].filter(Boolean).map((x) => `, ${x}`).join('')}
                  <LigneSource s={r.source} />
                </li>
              ))}
            </ul>
          )}
        </div>
        {s.aDemander.length > 0 && (
          <div className="remu-couche">
            <b>{t('remu.aDemander')}</b>
            <ul className="remu-liste">
              {s.aDemander.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </div>
        )}
        <details className="remu-sources">
          <summary>
            {t('remu.sources', {
              retenues: retenues.length === 1 ? t('remu.retenue1') : t('remu.retenues', { n: retenues.length }),
              ecartees: s.ecartees.length === 1 ? t('remu.ecartee1') : t('remu.ecarteesN', { n: s.ecartees.length }),
            })}
          </summary>
          <ul className="remu-liste">
            {retenues.map((r, i) => (
              <li key={i}>
                <a href={r.url ?? undefined} target="_blank" rel="noopener noreferrer">{domaine(r.url)}</a>
                <div className="field-help"><q>{r.extrait}</q></div>
              </li>
            ))}
          </ul>
          {s.ecartees.length > 0 && (
            <>
              <b>{t('remu.ecartees')}</b>
              <ul className="remu-liste">
                {s.ecartees.map((e, i) => (
                  <li key={i}>
                    {domaine(e.url) || t('remu.sourceOffre')} · {t(`remu.raison.${e.raison}` as const)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </details>
        <div className="relance-act">
          {cree && (
            <span className="field-help">
              {t('remu.estimeLe', { date: cree.toLocaleDateString(LOCALE_UI[lang], { day: 'numeric', month: 'long' }) })}
            </span>
          )}
          {peutChercher(etat?.permis ?? null) && <ActionButton size="sm" onClick={lancer}>{t('remu.refaire')}</ActionButton>}
        </div>
        <CompteurRecherches permis={etat?.permis ?? null} />
      </>
    )
  }

  return (
    <section className="card relance remuneration" id="remuneration" data-testid="bloc-remuneration">
      <div className="relance-h">
        <Coins size={18} />
        <b>{t('remu.titre')}</b>
      </div>
      {corps}
    </section>
  )
}
